import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Mail,
  Shield,
  Loader2,
  ArrowRight,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/api/axios';

type Step = 'new-email' | 'otp';

interface EmailChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  /** If provided, modal opens directly at the OTP step for this email */
  pendingEmail?: string;
  /** Called after OTP is sent — parent should show pending banner + update displayed email */
  onPending: (newEmail: string, verificationDeadline: string) => void;
  /** Called after OTP is verified — email change is confirmed */
  onVerified: (newEmail: string) => void;
}

export function EmailChangeModal({
  isOpen,
  onClose,
  currentEmail,
  pendingEmail,
  onPending,
  onVerified,
}: EmailChangeModalProps) {
  const [step, setStep] = useState<Step>('new-email');
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '', '', '']);
      setError('');
      if (pendingEmail) {
        // Open directly at OTP step for existing pending verification
        setNewEmail(pendingEmail);
        setStep('otp');
      } else {
        setNewEmail('');
        setStep('new-email');
      }
    }
  }, [isOpen, pendingEmail]);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleRequestChange = async () => {
    setError('');

    if (!newEmail.trim()) {
      setError('Please enter a new email address.');
      return;
    }

    if (!validateEmail(newEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError('This is already your current email.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await apiClient.post('/users/email/request-change', {
        newEmail,
      });
      const { verificationDeadline } = res.data;
      // Notify parent immediately — email is now pending
      onPending(newEmail, verificationDeadline);
      setStep('otp');
      toast.success('Verification code sent to your new email.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to send verification code. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }

    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const res = await apiClient.post('/users/email/verify-change', {
        otp: otpValue,
      });
      toast.success('Email updated successfully!');
      onVerified(res.data.email);
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Verification failed. Please try again.';
      setError(msg);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setIsLoading(true);
    try {
      await apiClient.post('/users/email/resend-otp');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
      toast.success('New verification code sent.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to resend code.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative z-10 w-full max-w-md rounded-xl border bg-white dark:bg-[#16151D] dark:border-white/10 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 dark:text-white/70"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {step === 'new-email' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white">
                  Change your email
                </h2>
                <p className="text-sm text-gray-500 dark:text-white/60">
                  Current: {currentEmail}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 dark:text-white/90">
                New email address
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleRequestChange()}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-purple-600 outline-none"
                placeholder="newemail@example.com"
                autoFocus
                disabled={isLoading}
              />
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-500/20 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                A verification code will be sent to your new email. You have 24
                hours to verify, or your email will revert to {currentEmail}.
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 mb-4 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestChange}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Send Code <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'otp' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white">
                  Verify your new email
                </h2>
                <p className="text-sm text-gray-500 dark:text-white/60">
                  Code sent to {newEmail}
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
              Enter the 6-digit code we sent to your new email address.
            </p>

            {/* OTP Input */}
            <div className="flex gap-2 justify-center mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData
                      .getData('text')
                      .replace(/\D/g, '')
                      .slice(0, 6);
                    if (text) handleOtpChange(0, text);
                  }}
                  className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 outline-none"
                  disabled={isLoading}
                />
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 mb-4 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="text-center mb-4">
              <button
                onClick={handleResendOtp}
                disabled={isLoading}
                className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 disabled:opacity-50"
              >
                Didn't receive it? Resend code
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleVerify}
                disabled={isLoading || otp.join('').length !== 6}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Verify <CheckCircle className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
